import { useEffect, useState } from 'react';

import { JobsService } from 'app/API';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@hooks/useAPI';

import { Button } from '@components/form-elements/buttons/Button';
import { AbsoluteSpinner } from '@components/spinners/Spinner';
import { ModalTitle } from '@components/Modal/components/ModalTitle';
import { ModalWrapper } from '@components/Modal/components/ModalWrapper';
import { GenericPageError } from '@components/GenericPageError';
import { Wrapper } from '@components/Modal/Admin/AdminBackupFilesModal/styles/Wrapper';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  id: string;
};

export const AdminBackupFilesModal = ({ onCancel, onSuccess, id }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const [files, setFiles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mixdownJobId, setMixdownJobId] = useState<string>();

  const onSubmit = async () => {
    setIsLoading(true);
    const mixdownJobId = await call(JobsService.adminMixdownJobBackupFiles({ id }));
    setMixdownJobId(mixdownJobId);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const files = await call(JobsService.adminGetJobBackupFiles({ id }));
      setFiles(files);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!mixdownJobId) {
      return;
    }
    const interval = setInterval(async () => {
      const status = await call(JobsService.adminGetMixdownJobBackupFilesStatus({ mixdownId: mixdownJobId }));
      if (status.returnvalue) {
        clearInterval(interval);
        closeModal();
        onSuccess();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [mixdownJobId]);

  if (!isLoading && !files.length) {
    return (
      <Wrapper>
        <ModalWrapper width="400">
          <ModalTitle>Attach Backup Files</ModalTitle>

          <GenericPageError message="No files to attach" />

          <div>
            <Button onClick={onCancel}>Ok</Button>
          </div>
        </ModalWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ModalWrapper width="400">
        {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
        <ModalTitle>Attach Backup Files</ModalTitle>

        <div className="files">
          {files.map((file, index) => (
            <div key={`${file}-${index}`} className="file">
              {file}
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={isLoading} onClick={onSubmit}>
            Attach
          </Button>
        </div>
      </ModalWrapper>
    </Wrapper>
  );
};
